import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { InfoButton, type InfoTopic } from "@/components/info-button";

/**
 * The one-tap pipe pick on a replacement's check-first line (Corbin
 * 2026-10-28). The draft couldn't tell which pipe goes back in — the customer
 * requires copper inside and Sch 80 PVC outside, but the device record doesn't
 * say which — so the line reads "Check pipe: copper or Sch 80 PVC?" and the
 * office answers here. One tap swaps the line (and its consumables) to that
 * pipe's words and price.
 */
export type RulePipe = "copper_press" | "pvc_80";

export interface PipeChoiceLine {
    id: string;
    estimate_id?: string | null;
    pipe_choice_state?: string | null;
    pipe_choice_material?: string | null;
    pipe_choice_hint?: string | null;
    pipe_choice_why?: string | null;
}

export const PIPE_CHOICES: Array<{ value: RulePipe; label: string }> = [
    { value: "copper_press", label: "Copper" },
    { value: "pvc_80", label: "Sch 80 PVC" },
];

export const PIPE_CHOICE_HELP: InfoTopic = {
    title: "Pick the pipe",
    lines: [
        "This customer requires a set pipe on replacements 2 inches and smaller.",
        "Inside above ground gets copper. Outside with any PVC gets Sch 80 PVC.",
        "The device record doesn't say which, so pick one. The price changes to match.",
        "To skip this next time, set Installed on the device's Specs.",
    ],
};

/** True when the line still waits on the office's pick. */
export function needsPipePick(l: PipeChoiceLine): boolean {
    return l.pipe_choice_state === "check";
}

/** Hook-free gate: most lines are not pipe picks, and the list renders anywhere. */
export function PipeChoiceStrip({ line, canEdit }: { line: PipeChoiceLine; canEdit: boolean }) {
    if (!needsPipePick(line) || !line.estimate_id) return null;
    return <PipePicker line={line} estimateId={line.estimate_id} canEdit={canEdit} />;
}

function PipePicker({ line, estimateId, canEdit }: { line: PipeChoiceLine; estimateId: string; canEdit: boolean }) {
    const qc = useQueryClient();
    const pick = useMutation({
        mutationFn: (material: RulePipe) =>
            api.post(`/api/estimates/${estimateId}/lines/${line.id}/pipe-choice`, { material }),
        onSuccess: async (_r, material) => {
            await qc.invalidateQueries({ queryKey: ["estimate", estimateId] });
            toast.success(`Pipe set: ${PIPE_CHOICES.find((c) => c.value === material)?.label ?? material}`);
        },
        onError: (e: any) =>
            toast.error(`NOT changed: ${e?.body?.message ?? e?.body?.error ?? e?.message ?? "unknown"}`),
    });

    return (
        <span
            className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[12px] text-amber-900"
            data-testid="pipe-choice-strip"
        >
            <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="font-semibold">Pick the pipe:</span>
            {PIPE_CHOICES.map((c) => (
                <button
                    key={c.value}
                    type="button"
                    disabled={!canEdit || pick.isPending}
                    onClick={() => pick.mutate(c.value)}
                    className={cn(
                        "inline-flex items-center gap-1 rounded-full border border-amber-400 bg-card px-2.5 py-0.5 font-semibold text-foreground hover:bg-amber-100 disabled:cursor-not-allowed",
                        line.pipe_choice_hint === c.value && "border-amber-600",
                    )}
                >
                    {c.label}
                    {line.pipe_choice_hint === c.value && (
                        <span className="text-[10.5px] font-normal text-amber-800">likely</span>
                    )}
                </button>
            ))}
            {pick.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-label="Saving" />}
            {line.pipe_choice_why && <span className="text-amber-800">{line.pipe_choice_why}.</span>}
            <InfoButton topic={PIPE_CHOICE_HELP} className="h-6 w-6 text-xs" />
        </span>
    );
}
